/**
 * SERPRO/VIO QR codes come in two outer framings. The top-level reader in the
 * VIO app (`m6.ve.a`) decides between them by trying to parse the first ten
 * bytes as ASCII hex; this module reproduces that decision.
 */
import { u32be, latin1Decode } from "./bytes";

/** How the QR header encodes its metadata, and the extracted values. */
export interface Framing {
  /** `hex` — ASCII `hex(timestamp,8) + hex(version,2)` prefix; `binary` — `uint32 id + version byte`. */
  framing: "hex" | "binary";
  /** Document creation time (unix seconds) for the `hex` framing, else `null`. */
  timestamp: number | null;
  /** 32-bit identifier for the `binary` framing, else `null`. */
  id: number | null;
  /** QR format version — selects the payload parser. */
  version: number;
  /** The bytes following the header, handed to the version parser. */
  payload: Uint8Array;
}

/**
 * Detects the QR framing and extracts `{ version, payload, timestamp | id }`.
 *
 * - **hex** — the first 8 bytes are the timestamp as ASCII hex and the next 2
 *   are the version as ASCII hex (e.g. `"611658b302"`). Payload is `bytes[10:]`.
 * - **binary** — otherwise, bytes `0..3` are a big-endian `uint32` id and byte
 *   `4` is the version. Payload is `bytes[5:]`. This is the classic CNH-e "v5"
 *   layout, whose `0x05` version byte is what stops it matching the hex form.
 *
 * @throws {Error} If the buffer is shorter than the 10-byte minimum header.
 */
export function detectFraming(bytes: Uint8Array): Framing {
  if (bytes.length < 10) {
    throw new Error("QR data too short to contain a header (need at least 10 bytes)");
  }

  const first10 = latin1Decode(bytes.subarray(0, 10));
  if (/^[0-9a-fA-F]{10}$/.test(first10)) {
    return {
      framing: "hex",
      timestamp: parseInt(first10.slice(0, 8), 16),
      id: null,
      version: parseInt(first10.slice(8, 10), 16),
      payload: bytes.subarray(10),
    };
  }

  return {
    framing: "binary",
    timestamp: null,
    id: u32be(bytes, 0),
    version: bytes[4]!,
    payload: bytes.subarray(5),
  };
}
